import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { yesterdayLocalDate, timezoneAbbr } from '../lib/timezones';
import { reportCacheConfig } from '../lib/reportCache';
import { ReportShell } from './reports/ReportView';
import { WeeklyReportView, type WeeklyReportData } from './reports/WeeklyReportView';
import { exportDomAsHtml, exportDomAsPdf, slugify } from '../lib/exportDomReport';

interface Props { customerId?: string }

function getThreatIntelType(row: any): string {
  const value =
    row?.IndicatorType ??
    row?.indicatorType ??
    row?.Type ??
    row?.type ??
    row?.Name ??
    row?.name ??
    row?.Category ??
    row?.category ??
    'Other';

  const text = String(value ?? '').trim();
  return text || 'Other';
}

function getThreatIntelCount(row: any): number {
  const value =
    row?.Count ??
    row?.count ??
    row?.Total ??
    row?.total ??
    0;

  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Weekly SOC Report tab. Fetches the (independent) `/weekly-report` endpoint
 * which transcribes the customer's "Weekly_SOC_Report" Logic App, then
 * renders the dedicated <WeeklyReportView /> — a structurally distinct
 * layout from the daily report.
 *
 * The `weekEnding` query parameter selects the LAST day (inclusive) of the
 * 7-day window, evaluated in the customer's timezone.
 */
export function WeeklyReportTab({ customerId: customerIdProp }: Props = {}) {
  const { activeCustomerId, activeCustomer, customers } = useCustomers();
  const customerId = customerIdProp ?? activeCustomerId;
  const customer = customerIdProp
    ? customers.find((c) => c.id === customerIdProp)
    : activeCustomer;

  const customerTz = customer?.timezone ?? 'UTC';
  const [weekEnding, setWeekEnding] = useState<string>(() => yesterdayLocalDate(customerTz));

  useEffect(() => {
    setWeekEnding(yesterdayLocalDate(customerTz));
  }, [customerTz]);

  const weeklyQuery = useQuery<WeeklyReportData, Error & { hint?: string }>({
    queryKey: ['weekly-report', customerId, weekEnding],
    queryFn: () => apiFetch<WeeklyReportData>(
      `/weekly-report?customerId=${encodeURIComponent(customerId ?? '')}&weekEnding=${encodeURIComponent(weekEnding)}`,
    ),
    enabled: !!customerId && customerId !== 'default',
    ...reportCacheConfig('weekly', weekEnding, customerTz),
  });
  const report = weeklyQuery.data ?? null;
  const loading = weeklyQuery.isLoading || weeklyQuery.isFetching;
  const error: string | null = (!customerId || customerId === 'default')
    ? 'Pick a specific customer to generate the weekly report. The MSSP "default" workspace is not supported here.'
    : weeklyQuery.error
      ? (weeklyQuery.error.hint ? `${weeklyQuery.error.message}. ${weeklyQuery.error.hint}` : weeklyQuery.error.message)
      : null;

  const reportTz = report?.timezone ?? customerTz;
  const tzAbbr = timezoneAbbr(reportTz);
  const customerName = customer?.name ?? customerId ?? 'Customer';

  // Defined BEFORE any early returns so the hook order stays stable across
  // the "no customer selected" guard. Both HTML and PDF clone the on-screen
  // report DOM (with all page CSS inlined) rather than redrawing it, so the
  // export always matches whatever the customer actually sees.
  const exportOpts = useCallback(() => {
    const exportDateStr = new Date().toISOString().slice(0, 10);
    return {
      rootSelector: '.daily-report-root',
      title: `${customerName} - Weekly SOC Report - week ending ${weekEnding}`,
      fileNameBase: `kloudynet-weekly-soc-report-${slugify(customerName)}-${weekEnding}-${exportDateStr}`,
      notMountedError: 'Weekly report content not visible - load a report first.',
    };
  }, [customerName, weekEnding]);

  const buildHtml = useCallback(async () => {
    if (!report) return { ok: false, error: 'Report not loaded.' };
    const exportedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    return exportDomAsHtml({
      ...exportOpts(),
      bannerHtml: `<div style="background:rgba(255,255,255,0.92);border-radius:12px;padding:14px 20px;margin-bottom:18px;color:#1e3c72;font-size:13px;box-shadow:0 4px 16px rgba(0,0,0,0.18);"><strong style="color:#0f172a;">${escapeHtml(customerName)}</strong> - Weekly SOC Report &middot; Week ending: <strong>${escapeHtml(weekEnding)}</strong> &middot; Exported ${escapeHtml(exportedAt)} UTC</div>`,
    });
  }, [report, exportOpts, customerName, weekEnding]);

  const buildPdf = useCallback(async () => {
    if (!report) return { ok: false, error: 'Report not loaded.' };
    return exportDomAsPdf(exportOpts());
  }, [report, exportOpts]);

  if (!customerId || customerId === 'default') {
    return (
      <div className="rounded-2xl bg-white p-8 text-slate-700 shadow-md">
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Weekly SOC Report</h2>
        <p className="text-sm">Select a specific customer from the dropdown above to generate their weekly report.</p>
      </div>
    );
  }

  return (
    <ReportShell
      toolbarTitle="Weekly SOC Report"
      customerName={customerName}
      controls={
        <label className="text-xs text-slate-600 dark:text-slate-300">
          <span className="block mb-1">Week ending ({tzAbbr})</span>
          <input
            type="date"
            value={weekEnding}
            max={yesterdayLocalDate(reportTz)}
            onChange={(e) => setWeekEnding(e.target.value)}
            className="rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
          />
        </label>
      }
      loading={loading}
      error={error}
      printDisabled={!report || loading}
      buildHtml={buildHtml}
      buildPdf={buildPdf}
    >
      {report && !loading && (
        <WeeklyReportView report={report} customerName={customerName} />
      )}
    </ReportShell>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
